import { z } from 'zod';

import { educationModuleTargetSchema } from '@entities/education-module';
import { educationActivityKeySchema } from '@entities/education-activity';

const nullableString = z.string().nullable().optional();

export const personalFlowCardSchema = z.looseObject({
  slide_id: z.string().min(1),
  slide_log_id: z.string().min(1).optional(),
  education_module_id: nullableString,
  program_id: nullableString,
  title: nullableString,
  image: nullableString,
  badge: nullableString,
  duration: z.union([z.string(), z.number()]).nullable().optional(),
  bubble_id_em: nullableString,
  bubble_id: z.string().min(1),
  workout: nullableString,
  program: nullableString,
  program_title: nullableString,
  program_flow_type: nullableString,
  em_type: nullableString,
  allow_manual_completion: z.boolean().optional(),
  logs: z.array(z.string()).optional().default([]),
  source_type: z
    .enum([
      'welcome_program',
      'main_program',
      'certification_registration',
      'onboarding_program',
      'online_course_registration',
      'news',
      'training_journal',
    ])
    .optional(),
  training_journal_context: z
    .object({
      personal_flow_source_type: z.enum([
        'welcome_program',
        'main_program',
        'certification_registration',
        'onboarding_program',
      ]),
      training_journal_entry_id: nullableString,
      slide_log_id: nullableString,
      training_program_id: z.string().min(1),
      training_program_title: nullableString,
      training_program_kind: nullableString,
      status: z.enum(['in_progress', 'completed']),
      review_state: z.enum(['needs_review', 'reviewed', 'not_required']),
      feedback_state: z.enum(['none', 'submitted']),
    })
    .optional(),
  registration_id: nullableString,
  registration_title: nullableString,
  activity_key: educationActivityKeySchema.optional(),
  activity_context: z
    .discriminatedUnion('kind', [
      z.object({
        kind: z.literal('certification_registration'),
        profile_id: z.uuid(),
        registration_id: z.string().min(1),
      }),
      z.object({
        kind: z.literal('onboarding_assignment'),
        profile_id: z.uuid(),
        assignment_id: z.string().nullable(),
        program_id: z.string().nullable(),
      }),
    ])
    .optional(),
  activity_state: z.enum(['available', 'completed']).optional(),
  activity_target: z
    .object({
      method: z.literal('GET'),
      path: z.string().startsWith('/api/v1/user-profiles/'),
    })
    .optional(),
  permitted_actions: z.array(z.enum(['open', 'review'])).optional(),
  target: educationModuleTargetSchema
    .extend({ kind: z.literal('education_module') })
    .nullable()
    .optional(),
});

export const personalFlowCurrentRegistrationProgressSchema = z.object({
  registration_id: z.string().min(1),
  registration_number: nullableString,
  course_type_id: nullableString,
  course_type_image: nullableString,
  title: nullableString,
  completed_steps: z.number().int().nonnegative(),
  total_steps: z.number().int().positive(),
  remaining_steps: z.number().int().nonnegative(),
  blocked_steps: z.number().int().nonnegative().nullable(),
  percent: z.number().min(0).max(100),
});

export const personalFlowCurrentTrainingProgramProgressSchema = z.object({
  training_program_id: z.string().min(1),
  title: nullableString,
  thumbnail: nullableString,
  completed_steps: z.number().int().nonnegative(),
  total_steps: z.number().int().positive(),
  remaining_steps: z.number().int().nonnegative(),
  blocked_steps: z.number().int().nonnegative().nullable(),
  percent: z.number().min(0).max(100),
});

export const certificationRegistrationProgressResponseSchema = z
  .object({
    data: z.object({
      registrationId: z.string().min(1),
      registrationNumber: z.string().nullable(),
      courseTypeId: z.string().nullable(),
      courseTypeImage: z.string().nullable(),
      title: z.string().nullable(),
      completedSteps: z.number().int().nonnegative(),
      totalSteps: z.number().int().positive(),
      remainingSteps: z.number().int().nonnegative(),
      blockedSteps: z.number().int().nonnegative().nullable(),
      percent: z.number().min(0).max(100),
    }),
  })
  .transform(({ data }) => ({
    data: {
      registration_id: data.registrationId,
      registration_number: data.registrationNumber,
      course_type_id: data.courseTypeId,
      course_type_image: data.courseTypeImage,
      title: data.title,
      completed_steps: data.completedSteps,
      total_steps: data.totalSteps,
      remaining_steps: data.remainingSteps,
      blocked_steps: data.blockedSteps,
      percent: data.percent,
    },
  }));

export const trainingProgramProgressResponseSchema = z
  .object({
    data: z.object({
      trainingProgramId: z.string().min(1),
      title: z.string().nullable(),
      thumbnail: z.string().nullable(),
      completedSteps: z.number().int().nonnegative(),
      totalSteps: z.number().int().nonnegative(),
      remainingSteps: z.number().int().nonnegative(),
      blockedSteps: z.number().int().nonnegative().nullable(),
      percent: z.number().min(0).max(100),
    }),
  })
  .transform(({ data }) => ({
    data: {
      training_program_id: data.trainingProgramId,
      title: data.title,
      thumbnail: data.thumbnail,
      completed_steps: data.completedSteps,
      total_steps: data.totalSteps,
      remaining_steps: data.remainingSteps,
      blocked_steps: data.blockedSteps,
      percent: data.percent,
    },
  }));

export const personalFlowPayloadSchema = z.looseObject({
  slides_progress: z
    .object({ slides: z.array(personalFlowCardSchema) })
    .nullable()
    .optional(),
  slides_completed: z
    .object({ slides: z.array(personalFlowCardSchema) })
    .nullable()
    .optional(),
  update_home_slides_time: z.string().optional(),
});

const compatibilitySchema = z.object({
  mode: z.enum(['bubble-workflow', 'bubble-data-api']),
  upstream_workflow: z.string(),
  upstream_status_code: z.number().int(),
});

const cacheSchema = z
  .looseObject({
    status: z.enum(['hit', 'miss', 'stale', 'warming', 'bypass', 'write_failed', 'read_failed']),
    isComplete: z.boolean().optional(),
  })
  .optional();

export const personalFlowFeedResponseSchema = z.looseObject({
  data: z.object({
    payload: personalFlowPayloadSchema,
    compatibility: compatibilitySchema,
  }),
  meta: z
    .looseObject({
      sourceDetail: z.string().optional(),
      cache: cacheSchema,
    })
    .optional(),
});

const personalFlowScopedContextSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('certification_registration'),
    registration_ref: z.string().min(1),
  }),
  z.object({
    kind: z.literal('training_program'),
    program_ref: z.string().min(1),
  }),
  z.object({ kind: z.literal('general') }),
]);

export const personalFlowScopedItemsResponseSchema = z.looseObject({
  data: z.object({
    context: personalFlowScopedContextSchema,
    payload: personalFlowPayloadSchema,
  }),
  meta: z
    .looseObject({
      sourceDetail: z.string().optional(),
      cache: cacheSchema,
    })
    .optional(),
});

export const registrationSchema = z.looseObject({
  registration_id: z.string().min(1).optional(),
  user_course_id: z.string().min(1).optional(),
  is_current: z.boolean().optional(),
  number: z.union([z.string(), z.number()]).nullable().optional(),
  registration_number: z.union([z.string(), z.number()]).nullable().optional(),
  title: nullableString,
  registration_title: nullableString,
  registration_pic: nullableString,
  course_type_image: nullableString,
  courseTypeImage: nullableString,
  instructor: z
    .union([
      z.string(),
      z.object({
        status: z.enum(['assigned', 'not_assigned', 'unavailable']),
        source: z
          .enum(['registration_submitting', 'registration_certifying', 'course_submitting'])
          .nullable(),
        user_id: z.string().nullable(),
        display_name: z.string().nullable(),
      }),
    ])
    .nullable()
    .optional(),
  instructor_name: nullableString,
});

export const personalFlowHomeResponseSchema = z.looseObject({
  data: z.object({
    payload: z.looseObject({
      current_registration_id: nullableString,
      registrations: z.array(registrationSchema).optional().default([]),
      main_program: nullableString,
      my_bt_programs: z.array(z.string()).optional().default([]),
    }),
    compatibility: compatibilitySchema,
  }),
});

export const personalFlowProgramSchema = z.looseObject({
  bubble_id: z.string().min(1).optional(),
  id: z.string().min(1).optional(),
  training_program_id: z.string().min(1).optional(),
  title: nullableString,
  cover: nullableString,
  image: nullableString,
  thumbnail: nullableString,
  totalSlides: z.number().nullable().optional(),
  slidesDone: z.number().nullable().optional(),
});

export const personalFlowProgramsResponseSchema = z.looseObject({
  data: z.object({
    payload: z.looseObject({
      total: z.number().int().nonnegative().optional(),
      bt_program: z.array(personalFlowProgramSchema).optional().default([]),
    }),
    compatibility: compatibilitySchema,
  }),
});

export const personalFlowMutationResponseSchema = z.looseObject({
  data: z.object({
    payload: z.looseObject({
      accepted: z.boolean().optional(),
      persisted: z.boolean().optional(),
      changed: z.boolean().optional(),
      operation: z.string().optional(),
    }),
    compatibility: compatibilitySchema,
  }),
});

export type PersonalFlowCard = z.infer<typeof personalFlowCardSchema>;
export type PersonalFlowCurrentRegistrationProgress = z.infer<
  typeof personalFlowCurrentRegistrationProgressSchema
>;
export type PersonalFlowCurrentTrainingProgramProgress = z.infer<
  typeof personalFlowCurrentTrainingProgramProgressSchema
>;
export type PersonalFlowFeedResponse = z.infer<typeof personalFlowFeedResponseSchema>;
export type PersonalFlowScopedItemsResponse = z.infer<typeof personalFlowScopedItemsResponseSchema>;
export type PersonalFlowHomeResponse = z.infer<typeof personalFlowHomeResponseSchema>;
export type PersonalFlowProgram = z.infer<typeof personalFlowProgramSchema>;
export type PersonalFlowProgramsResponse = z.infer<typeof personalFlowProgramsResponseSchema>;
export type PersonalFlowMutationResponse = z.infer<typeof personalFlowMutationResponseSchema>;
